import type { PromiseWithServerResult } from "@rocicorp/zero"
import { ZeroConnectionError } from "./zero-connection-error"

// Zero 1.x client/server promises always resolve; a failed mutation resolves with
// { type: "error" }. Await the chosen promise and throw so callers see failures.
export async function mutateOk(
	result: PromiseWithServerResult,
	wait: "client" | "server" = "client",
) {
	const res = await result[wait]
	if (res.type === "error") {
		if (res.error.type === "zero")
			throw new ZeroConnectionError(res.error.message)
		throw new Error(res.error.message)
	}
	return res
}

export class MutationUnconfirmedError extends Error {
	name = "MutationUnconfirmedError"
}

export const SERVER_CONFIRM_TIMEOUT_MS = 10_000

// A push the server never answers (a WAF block, a stuck client group) keeps the
// optimistic row on screen indefinitely, so a write that must persist stops
// waiting and throws instead of looking saved.
export async function mutateConfirmed(
	result: PromiseWithServerResult,
	timeoutMs = SERVER_CONFIRM_TIMEOUT_MS,
) {
	await mutateOk(result, "client")
	let timer: ReturnType<typeof setTimeout> | undefined
	const timeout = new Promise<never>((_, reject) => {
		timer = setTimeout(
			() =>
				reject(
					new MutationUnconfirmedError(
						`Server did not confirm the mutation within ${timeoutMs}ms`,
					),
				),
			timeoutMs,
		)
	})
	try {
		return await Promise.race([mutateOk(result, "server"), timeout])
	} finally {
		clearTimeout(timer)
	}
}

// Awaiting the server result on a keystroke path makes typing lag, so watch it
// out of band: a rejected mutation otherwise still looks saved in the UI.
export function reportServerRejection(
	result: PromiseWithServerResult,
	onRejected: (message: string, error: Error) => void,
): PromiseWithServerResult {
	void result.server
		.then((res) => {
			if (res.type !== "error") return
			onRejected(
				res.error.message,
				res.error.type === "zero"
					? new ZeroConnectionError(res.error.message)
					: new Error(res.error.message),
			)
		})
		.catch((error: unknown) => {
			const message = error instanceof Error ? error.message : String(error)
			onRejected(message, error instanceof Error ? error : new Error(message))
		})
	return result
}
